import { type Evaluated } from '@/domain/construction';
import { type EntityId, solidBehind, solidBehindAll } from '@/domain/model';
import { axisInPlane, type Plane, planeThrough } from '@/domain/solid';

import {
  aCircle,
  aCurve,
  aLinear,
  aMirror,
  anArm,
  aPlane,
  aPoint,
  aRegion,
  aSegment,
  aVector,
  aSolidPoint,
  ConstructionTool,
} from './ConstructionTool';
import { type Tool, type ToolContext } from './Tool';

/**
 * The construction tool registry.
 *
 * Every tool here is a declaration, not an implementation: the pick sequence and the
 * source to build. The behaviour — filtering, previewing, refusing a repeat pick, creating
 * only what evaluates — lives once in `ConstructionTool`.
 *
 * The underlying operations were built and tested in M5. Nothing in this file computes
 * geometry; it decides what the user clicks and in what order.
 */

/**
 * Reads a pick by position.
 *
 * Throws rather than returning a placeholder: `ConstructionTool` only calls `build` once
 * every step has been picked, so a gap here means the tool's step list and its build
 * function disagree — a coding error worth failing loudly, not a state to tolerate.
 */
const at = (ids: readonly EntityId[], index: number): EntityId => {
  const id = ids[index];
  if (id === undefined) throw new Error(`construction expected a pick at index ${String(index)}`);
  return id;
};

/* ------------------------------------------------------------------ from points */

export function createMidpointTool(): Tool {
  return new ConstructionTool(
    'midpoint',
    'Trung điểm',
    'm',
    'Dựng hình',
    'mid',
    [aPoint('điểm thứ nhất'), aPoint('điểm thứ hai')],
    (ids) => (ids.length !== 2 ? null : { op: 'midpoint', a: at(ids, 0), b: at(ids, 1) })
  );
}

export function createSegmentMidpointTool(): Tool {
  return new ConstructionTool(
    'segment-midpoint',
    'Trung điểm của đoạn',
    'M',
    'Dựng hình',
    'mid',
    [aSegment()],
    (ids) => (ids.length !== 1 ? null : { op: 'segment-midpoint', segment: at(ids, 0) })
  );
}

export function createPerpendicularBisectorTool(): Tool {
  return new ConstructionTool(
    'perpendicular-bisector',
    'Trung trực',
    'B',
    'Dựng hình',
    'pb',
    [aPoint('điểm thứ nhất'), aPoint('điểm thứ hai')],
    (ids) =>
      ids.length !== 2 ? null : { op: 'perpendicular-bisector', a: at(ids, 0), b: at(ids, 1) }
  );
}

export function createAngleBisectorTool(): Tool {
  return new ConstructionTool(
    'angle-bisector',
    'Phân giác',
    'A',
    'Dựng hình',
    'bis',
    // Vertex second, matching how an angle is named: ∠A-V-C.
    [
      aPoint('một điểm trên cạnh thứ nhất'),
      aPoint('ĐỈNH của góc'),
      aPoint('một điểm trên cạnh thứ hai'),
    ],
    (ids) =>
      ids.length !== 3
        ? null
        : { op: 'angle-bisector', a: at(ids, 0), vertex: at(ids, 1), c: at(ids, 2) }
  );
}

/* -------------------------------------------------------- point relative to a curve */

export function createPerpendicularTool(): Tool {
  return new ConstructionTool(
    'perpendicular',
    'Đường vuông góc',
    'e',
    'Dựng hình',
    'perp',
    [aLinear('đường cần vuông góc với'), aPoint('điểm mà đường đi qua')],
    (ids) =>
      ids.length !== 2
        ? null
        : { op: 'perpendicular-line', reference: at(ids, 0), through: at(ids, 1) }
  );
}

export function createParallelTool(): Tool {
  return new ConstructionTool(
    'parallel',
    'Đường song song',
    'a',
    'Dựng hình',
    'par',
    [aLinear('đường cần song song với'), aPoint('điểm mà đường đi qua')],
    (ids) =>
      ids.length !== 2 ? null : { op: 'parallel-line', reference: at(ids, 0), through: at(ids, 1) }
  );
}

export function createProjectionTool(): Tool {
  return new ConstructionTool(
    'projection',
    'Hình chiếu',
    'j',
    'Dựng hình',
    'proj',
    [aPoint('điểm cần chiếu'), aCurve('đường để chiếu lên')],
    (ids) =>
      ids.length !== 2 ? null : { op: 'projected-point', point: at(ids, 0), onto: at(ids, 1) }
  );
}

/**
 * Both tangents from an external point.
 *
 * Two sources are offered and the resolver keeps whichever exist, so a point outside the
 * circle yields two lines, a point on it yields one, and a point inside yields none — with
 * no branching here.
 */
export function createTangentTool(): Tool {
  return new ConstructionTool(
    'tangent',
    'Tiếp tuyến',
    'T',
    'Dựng hình',
    'tan',
    [aCircle('đường tròn'), aPoint('điểm kẻ tiếp tuyến từ đó')],
    (ids) =>
      ids.length !== 2
        ? null
        : [
            { op: 'tangent-line', conic: at(ids, 0), from: at(ids, 1), index: 0 },
            { op: 'tangent-line', conic: at(ids, 0), from: at(ids, 1), index: 1 },
          ]
  );
}

/**
 * Every intersection of two curves.
 *
 * Four branch indices are offered because a circle can meet an ellipse four times; the
 * resolver discards the ones that do not exist, so a pair of lines still yields exactly
 * one point.
 */
export function createIntersectionTool(): Tool {
  return new ConstructionTool(
    'intersection',
    'Giao điểm',
    'x',
    'Dựng hình',
    'int',
    [aCurve('đường thứ nhất'), aCurve('đường thứ hai')],
    (ids) =>
      ids.length !== 2
        ? null
        : [0, 1, 2, 3].map((index) => ({
            op: 'intersection' as const,
            a: at(ids, 0),
            b: at(ids, 1),
            index,
          }))
  );
}

/* ------------------------------------------------------------- triangle centres */

const TRIANGLE_CENTRES = [
  { op: 'circumcenter', id: 'circumcenter', label: 'Tâm ngoại tiếp', shortcut: 'O' },
  { op: 'incenter', id: 'incenter', label: 'Tâm nội tiếp', shortcut: 'I' },
  { op: 'centroid', id: 'centroid', label: 'Trọng tâm', shortcut: 'G' },
  { op: 'orthocenter', id: 'orthocenter', label: 'Trực tâm', shortcut: 'H' },
] as const;

export function createTriangleCentreTools(): readonly Tool[] {
  return TRIANGLE_CENTRES.map(
    ({ op, id, label, shortcut }) =>
      new ConstructionTool(
        id,
        label,
        shortcut,
        'Tâm',
        id.slice(0, 3),
        [aPoint('đỉnh thứ nhất'), aPoint('đỉnh thứ hai'), aPoint('đỉnh thứ ba')],
        (ids) => (ids.length !== 3 ? null : { op, a: at(ids, 0), b: at(ids, 1), c: at(ids, 2) })
      )
  );
}

/* --------------------------------------------------------------------- circles */

export function createCircumcircleTool(): Tool {
  return new ConstructionTool(
    'circumcircle',
    'Đường tròn qua 3 điểm',
    'k',
    'Đường cong',
    'cir',
    [aPoint('điểm thứ nhất'), aPoint('điểm thứ hai'), aPoint('điểm thứ ba')],
    (ids) =>
      ids.length !== 3
        ? null
        : { op: 'circle-3-points', a: at(ids, 0), b: at(ids, 1), c: at(ids, 2) }
  );
}

export function createArcThroughTool(): Tool {
  return new ConstructionTool(
    'arc-3-points',
    'Cung qua 3 điểm',
    'D',
    'Đường cong',
    'arc',
    [aPoint('điểm đầu'), aPoint('một điểm trên cung'), aPoint('điểm cuối')],
    (ids) =>
      ids.length !== 3 ? null : { op: 'arc-3-points', a: at(ids, 0), b: at(ids, 1), c: at(ids, 2) }
  );
}

export function createArcCentreTool(): Tool {
  return new ConstructionTool(
    'arc-center',
    'Cung từ tâm',
    'R',
    'Đường cong',
    'arc',
    [aPoint('tâm'), aPoint('điểm đầu'), aPoint('điểm cuối')],
    (ids) =>
      ids.length !== 3
        ? null
        : {
            op: 'arc-center',
            center: at(ids, 0),
            start: at(ids, 1),
            end: at(ids, 2),
            counterclockwise: true,
          }
  );
}

export function createCircleCentreTool(): Tool {
  return new ConstructionTool(
    'circle-center',
    'Tâm đường tròn',
    'C',
    'Đường cong',
    'ctr',
    [aCircle()],
    (ids) => (ids.length !== 1 ? null : { op: 'circle-center', circle: at(ids, 0) })
  );
}

export function createEllipseFociTool(): Tool {
  return new ConstructionTool(
    'ellipse-foci',
    'Elip từ 2 tiêu điểm',
    'E',
    'Đường cong',
    'ell',
    [aPoint('tiêu điểm thứ nhất'), aPoint('tiêu điểm thứ hai'), aPoint('một điểm trên elip')],
    (ids) =>
      ids.length !== 3
        ? null
        : { op: 'ellipse-foci', f1: at(ids, 0), f2: at(ids, 1), through: at(ids, 2) }
  );
}

/* ------------------------------------------------------------------ transforms */

export function createReflectionTool(): Tool {
  return new ConstructionTool(
    'reflection',
    'Đối xứng trục',
    'F',
    'Biến đổi',
    'ref',
    [aPoint('điểm cần lấy đối xứng'), aMirror()],
    (ids) =>
      ids.length !== 2 ? null : { op: 'reflected-point', point: at(ids, 0), mirror: at(ids, 1) }
  );
}

export function createTranslationTool(): Tool {
  return new ConstructionTool(
    'translation',
    'Tịnh tiến',
    'V',
    'Biến đổi',
    'trn',
    [aPoint('điểm cần tịnh tiến'), aVector('vector tịnh tiến')],
    (ids) =>
      ids.length !== 2 ? null : { op: 'translated-point', point: at(ids, 0), vector: at(ids, 1) }
  );
}

/**
 * Rotation and scaling by a fixed amount.
 *
 * The angle and factor are baked in rather than prompted for. A modal dialogue mid-gesture
 * is the wrong shape for a direct-manipulation editor, and the quarter turn and doubling
 * are what a geometry lesson reaches for; anything else is a property away once the object
 * exists. When the properties panel learns to edit construction parameters these become
 * fully general without changing the tool.
 */
export function createRotationTool(): Tool {
  return new ConstructionTool(
    'rotation',
    'Quay 90°',
    'Q',
    'Biến đổi',
    'rot',
    [aPoint('điểm cần quay'), aPoint('tâm quay')],
    (ids) =>
      ids.length !== 2
        ? null
        : {
            op: 'rotated-point',
            point: at(ids, 0),
            center: at(ids, 1),
            angle: Math.PI / 2,
          }
  );
}

export function createScalingTool(): Tool {
  return new ConstructionTool(
    'scaling',
    'Vị tự ×2',
    'X',
    'Biến đổi',
    'scl',
    [aPoint('điểm cần vị tự'), aPoint('tâm vị tự')],
    (ids) =>
      ids.length !== 2
        ? null
        : { op: 'scaled-point', point: at(ids, 0), center: at(ids, 1), factor: 2 }
  );
}

/* ---------------------------------------------------------------- annotation */

/**
 * Marks an angle.
 *
 * **Vertex first, then the two sides.** The first version asked for three points in the
 * order an angle is *named* — ∠A-V-C — which reads well and works badly:
 *
 * - The vertex is what a user is thinking about, so asking for it second means they hold
 *   the answer in their head while hunting for something else.
 * - A point can be the vertex of several angles at once. Naming it first settles which
 *   angle is meant; naming it second leaves the tool guessing until the last click.
 * - The prompt said "a point on the first arm", and there are no points along a side —
 *   only at its ends. Hovering the side, which is what everybody tries, found nothing.
 *
 * Sides are now pickable directly, which is the gesture the tool should have had from the
 * start.
 *
 * The preview is doing real work here. `ConstructionTool` evaluates what it would create and
 * draws it, so hovering the third point shows the **square** the moment the angle is right
 * and an arc when it is not. The user learns the rule before committing to anything, and the
 * mark they get is the mark they saw.
 */
export function createAngleMarkTool(): Tool {
  return new ConstructionTool(
    'angle-mark',
    'Ký hiệu góc',
    'z',
    'Ký hiệu',
    'ang',
    // Vertex first: it is the thing the user is already thinking about, and naming it first
    // settles which of the several angles meeting at that point is meant.
    [aPoint('ĐỈNH của góc'), anArm('cạnh thứ nhất'), anArm('cạnh thứ hai')],
    (ids) =>
      ids.length !== 3
        ? null
        : {
            op: 'angle-mark',
            vertex: at(ids, 0),
            a: at(ids, 1),
            c: at(ids, 2),
            ticks: 1,
            showValue: false,
          },
    'cạnh này không đi qua đỉnh vừa chọn'
  );
}

/**
 * Marks an angle and writes its size.
 *
 * A separate tool rather than a setting, because "mark it" and "mark it and say 30°" are two
 * different intentions and a user has one of them in mind before they click. Both are
 * adjustable afterwards from the panel.
 */
export function createAngleValueTool(): Tool {
  return new ConstructionTool(
    'angle-value',
    'Ký hiệu góc + số đo',
    'Z',
    'Ký hiệu',
    'ang',
    [aPoint('ĐỈNH của góc'), anArm('cạnh thứ nhất'), anArm('cạnh thứ hai')],
    (ids) =>
      ids.length !== 3
        ? null
        : {
            op: 'angle-mark',
            vertex: at(ids, 0),
            a: at(ids, 1),
            c: at(ids, 2),
            ticks: 1,
            showValue: true,
          },
    'cạnh này không đi qua đỉnh vừa chọn'
  );
}

/* ------------------------------------------------------------ combining shapes */

const COMBINE = [
  { operation: 'union', id: 'combine-union', label: 'Hợp', shortcut: 'U' },
  { operation: 'intersection', id: 'combine-intersect', label: 'Giao', shortcut: 'N' },
  { operation: 'difference', id: 'combine-subtract', label: 'Hiệu', shortcut: 'S' },
] as const;

/**
 * Combines two polygons into one.
 *
 * The result is a live construction, not a baked shape: drag a vertex of either operand
 * and the combination reshapes. That is what makes it useful in a lesson — the point of
 * showing a union is watching it change.
 *
 * It evaluates to nothing when the result needs more than one ring, which the preview
 * shows before the click. A hole or a split result has no representation in `Evaluated`
 * yet, and quietly returning the largest piece would be a wrong answer that looked right.
 */
export function createCombineTools(): readonly Tool[] {
  return COMBINE.map(
    ({ operation, id, label, shortcut }) =>
      new ConstructionTool(
        id,
        label,
        shortcut,
        'Ghép hình',
        'comb',
        [aRegion('đa giác thứ nhất'), aRegion('đa giác thứ hai')],
        (ids) =>
          ids.length !== 2
            ? null
            : { op: 'polygon-boolean', operation, a: at(ids, 0), b: at(ids, 1) }
      )
  );
}

/* ------------------------------------------------------------------- planes */

/**
 * A plane, by clicking three of the figure's corners.
 *
 * The mouse counterpart of `plane S A C`, and the reason both exist: a teacher building one
 * figure reaches for the mouse, and one writing four variants of a problem reaches for the
 * script. Neither should be the poor relation.
 *
 * The solid is not asked for. It is found from the three picks — see {@link solidBehindAll} —
 * so the gesture is exactly "point at three corners", which is what the sentence in the problem
 * says. Corners of two different figures build nothing, and `ConstructionTool` already refuses
 * a pick that would build nothing, so the third corner of a second solid simply will not take.
 */
export function createPlaneTool(): Tool {
  return new ConstructionTool(
    'plane',
    'Mặt phẳng',
    'f',
    'Dựng hình',
    'plane',
    [
      aSolidPoint('đỉnh của một hình khối — mặt phẳng ở đây là thiết diện cắt khối'),
      aSolidPoint('đỉnh thứ hai, của cùng khối đó'),
      aSolidPoint('đỉnh thứ ba'),
    ],
    (ids, context) => {
      if (ids.length !== 3) return null;
      const solid = solidBehindAll(context.document, ids);
      // Three points of no solid are three marks on a page, and they name no plane in space.
      if (solid === null) return null;

      return { op: 'plane', solid, a: at(ids, 0), b: at(ids, 1), c: at(ids, 2) };
    },
    /*
     * Named rather than left to the default "không dựng được với lựa chọn này", and it points at
     * the way out rather than only at the wall.
     *
     * There are two ways to get here — the three points belong to no solid, or to two — and one
     * sentence covers both. The important half is the second: a teacher whose figure is drawn by
     * hand, from points and segments, is not doing anything wrong, and the answer for them is a
     * toned polygon. That draws the *same* plane; what it cannot do is compute where the plane
     * cuts, which is the whole of what this tool is for.
     */
    /*
     * Only one cause is left. Points belonging to *no* solid can no longer be picked at all —
     * `aSolidPoint` refuses them on the first hover — so reaching here means three corners that
     * each belong to a solid but not to the **same** one, and a plane through corners of two
     * separate figures is a sentence with no meaning.
     */
    'ba đỉnh phải thuộc cùng một khối — mặt phẳng là thiết diện của khối đó'
  );
}

/**
 * A plane turned off another, about a line in it — the only construction here that states an
 * *angle* rather than a position.
 *
 * ## Why it arrives at sixty and is edited afterwards
 *
 * `ConstructionTool` collects **picks**, and an angle is not one. Every other way round is worse:
 * a dialog before the third click hides the drawing at the moment the user most wants to see it,
 * and a drag names "about sixty" where a problem says sixty exactly. So the click produces a real
 * plane at the commonest angle a paper states, and `PlanePanel` is where 60 becomes 45 — the same
 * shape as a solid, which arrives at `DEFAULT_EXTENT` and is resized from its panel.
 *
 * ## Which direction
 *
 * The axis runs from the second pick to the third, and that direction is the sign. Half the time
 * the first answer is the empty one — turning the floor of a box about its own edge tips the plane
 * out through the bottom — and the fix is the panel's reverse button, or picking the two ends the
 * other way round. Naming the rule beats a hidden convention: this is the ambiguity Onshape's
 * "Line Angle Plane" has left unresolved for nine years.
 */
export function createTurnPlaneTool(): Tool {
  return new ConstructionTool(
    'plane-turn',
    'Mặt phẳng nghiêng',
    'q',
    'Dựng hình',
    'pt',
    [
      aPlane('một mặt phẳng đã dựng, để quay nó'),
      aSolidPoint('một đỉnh trên trục quay'),
      aSolidPoint('đỉnh kia của trục quay'),
    ],
    (ids, context) => {
      if (ids.length !== 3) return null;
      const [from, a, b] = ids;
      if (from === undefined || a === undefined || b === undefined) return null;

      /*
       * The solid comes from the **plane**, not from the two points.
       *
       * `solidBehindAll` — what `createPlaneTool` uses — *skips* a pick that traces back to no
       * solid rather than refusing it, so a free mark among the picks would still answer, with a
       * "corner" that has no position in space. The base plane cannot be that: it was cut into a
       * body and remembers which, so asking it is both shorter and impossible to fool.
       */
      const solid = solidBehind(context.document, from);
      if (solid === null) return null;

      return { op: 'plane-turn', solid, from, a, b, degrees: DEFAULT_TURN };
    },
    turnRefusal
  );
}

/** The angle a turned plane arrives at. After 45°, the commonest number a paper states. */
const DEFAULT_TURN = 60;

/**
 * Why a turn built nothing — three causes, and they want different answers.
 *
 * The generic "không dựng được với lựa chọn này" is true of all three and useful for none. Two of
 * them are not about the clicks at all: the picks were right and it is the *number* or its sign
 * that wants changing, and a message blaming the clicks sends the user back to re-pick corners
 * that were never the problem.
 *
 * The round case is the one that most needs saying. `sectionValue` refuses every oblique plane
 * through a cone or a cylinder on purpose — the section is a conic truncated by the flat ends,
 * which no value here can express — so the figure would simply be empty with nothing to explain
 * it. It is also exactly the shape of the corpus's own "một mặt phẳng hợp với đáy một góc 60°"
 * problem, so it is the first thing a teacher will try.
 */
const turnRefusal = (ids: readonly EntityId[], context: ToolContext): string => {
  const [fromId, a, b] = ids;
  if (fromId === undefined || a === undefined || b === undefined) {
    return 'chọn một mặt phẳng rồi hai điểm làm trục quay';
  }

  const solidId = solidBehind(context.document, fromId);
  const solid = solidId === null ? null : context.scene.values.get(solidId);

  if (solid?.kind === 'solid' && solid.round !== undefined) {
    return (
      'khối tròn chỉ cắt được bằng mặt phẳng vuông góc với trục hoặc chứa trục — ' +
      'cắt xiên hình nón cho một đường conic mà hình vẽ không biểu diễn được. ' +
      'Hãy dựng thiết diện qua hai đường sinh.'
    );
  }

  const plane = planeOfValue(context.scene.values.get(fromId));
  const from = context.scene.values.get(a);
  const to = context.scene.values.get(b);

  if (
    plane !== null &&
    from?.kind === 'point' &&
    from.in3d !== undefined &&
    to?.kind === 'point' &&
    to.in3d !== undefined &&
    !axisInPlane(plane, from.in3d, to.in3d)
  ) {
    return 'trục quay phải là hai điểm nằm trong chính mặt phẳng được quay';
  }

  return `quay ${String(DEFAULT_TURN)}° theo hướng này thì mặt phẳng ra ngoài khối — chọn hai đầu trục theo thứ tự ngược lại`;
};

/** The plane a shaded section lies in, recovered from its corners in space. */
function planeOfValue(value: Evaluated | null | undefined): Plane | null {
  if (value?.kind !== 'polygon' || value.in3d === undefined) return null;
  const [first, second] = value.in3d;
  if (first === undefined || second === undefined) return null;
  for (const point of value.in3d.slice(2)) {
    const found = planeThrough(first, second, point);
    if (found !== null) return found;
  }
  return null;
}

/**
 * The giao tuyến of two planes, by clicking both.
 *
 * Drawn as the segment inside the solid, because the line itself runs off the paper. `(SAC)` and
 * `(SBD)` give the height of the pyramid, and nothing about the gesture says where that is.
 */
export function createPlaneMeetTool(): Tool {
  return new ConstructionTool(
    'plane-meet',
    'Giao tuyến',
    'i',
    'Dựng hình',
    'meet',
    [aPlane('mặt phẳng thứ nhất'), aPlane('mặt phẳng thứ hai')],
    (ids, context) => {
      if (ids.length !== 2) return null;
      const solid = solidBehind(context.document, at(ids, 0));
      if (solid === null || solid !== solidBehind(context.document, at(ids, 1))) return null;

      return { op: 'plane-meet', solid, a: at(ids, 0), b: at(ids, 1) };
    }
  );
}

/**
 * The foot of the perpendicular from a point to a plane: click the point, then the plane.
 *
 * The construction every "khoảng cách từ S đến (ABCD)" is about, and the one the height of a
 * pyramid is drawn to. The point is picked first because that is the order the sentence has it.
 */
export function createPlaneFootTool(): Tool {
  return new ConstructionTool(
    'plane-foot',
    'Chân vuông góc xuống mặt phẳng',
    'n',
    'Dựng hình',
    'h',
    [aPoint('điểm cần hạ'), aPlane('một mặt phẳng đã dựng')],
    (ids, context) => {
      if (ids.length !== 2) return null;
      const plane = context.document.entities.get(at(ids, 1));
      if (plane?.source.op !== 'plane' && plane?.source.op !== 'plane-parallel') return null;

      return { op: 'plane-foot', solid: plane.source.solid, plane: at(ids, 1), from: at(ids, 0) };
    }
  );
}

/** Every construction tool, grouped the way the toolbar shows them. */
export function createConstructionTools(): readonly Tool[] {
  return [
    createMidpointTool(),
    createSegmentMidpointTool(),
    createPerpendicularTool(),
    createParallelTool(),
    createPerpendicularBisectorTool(),
    createAngleBisectorTool(),
    createTangentTool(),
    createProjectionTool(),
    createIntersectionTool(),
    createPlaneTool(),
    createTurnPlaneTool(),
    createPlaneMeetTool(),
    createPlaneFootTool(),
    ...createTriangleCentreTools(),
    createCircumcircleTool(),
    createCircleCentreTool(),
    createArcThroughTool(),
    createArcCentreTool(),
    createEllipseFociTool(),
    createReflectionTool(),
    createRotationTool(),
    createTranslationTool(),
    createScalingTool(),
    ...createCombineTools(),
    createAngleMarkTool(),
    createAngleValueTool(),
  ];
}
